/**
 * Sprint T-16.1 · Automation Center · EventsTab.
 *
 * Timeline paginado de eventos · manual refresh (§17.4).
 */
import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Filter, RefreshCw } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useEvents } from "../../hooks/useAutomation";
import { EventRow } from "./shared/DomainCards";

// Keep in sync with backend/automation/events.py KNOWN_EVENTS
const KNOWN_EVENTS = [
  "automation.job.scheduled",
  "automation.job.started",
  "automation.job.succeeded",
  "automation.job.failed",
  "automation.job.retried",
  "automation.job.dlq",
  "automation.job.paused",
  "automation.job.resumed",
  "ag02.growth.refreshed",
  "ag03.seo.refreshed",
  "ag04.content.refreshed",
  "ag05.competitor.refreshed",
  "ag06.marketplace.refreshed",
  "reporting.weekly_summary.generated",
  "reporting.monthly_report.generated",
  "system.health.pinged",
  "system.tokens.cleaned",
  "system.temp_files.cleaned",
  "marketplace.reindex.completed",
  "compliance.retention.checked",
];

const PAGE_SIZE = 50;


export default function EventsTab() {
  const [selectedEvent, setSelectedEvent] = useState("");
  const [page, setPage] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const q = useEvents({
    event_name: selectedEvent || undefined,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });
  // Enable poll ONLY when toggled ON
  React.useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => q.refetch(), 15000);
    return () => clearInterval(id);
  }, [autoRefresh, q]);

  const items = q.data?.items || [];
  const total = q.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="space-y-4" data-testid="automation-events-tab">
      <WidgetCard
        title="Filtros"
        description="Filtrar por event_name canónico · retención 30d."
        citations={["/api/automation/events"]}
        testId="widget-events-filters"
      >
        <div className="flex flex-wrap items-end gap-2" data-testid="events-filter-bar">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-[11px] text-[var(--ag-text-dim)]">event_name</label>
            <select
              data-testid="events-filter-event"
              value={selectedEvent}
              onChange={(e) => {
                setSelectedEvent(e.target.value);
                setPage(0);
              }}
              className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1.5 font-mono text-xs"
            >
              <option value="">Todos</option>
              {KNOWN_EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {ev}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            data-testid="events-refresh-btn"
            onClick={() => q.refetch()}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-3 py-1.5 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            <RefreshCw className={`h-3 w-3 ${q.isFetching ? "animate-spin" : ""}`} /> Refrescar
          </button>
          <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
            <input
              data-testid="events-autorefresh-toggle"
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            auto-refresh 15s
          </label>
        </div>
      </WidgetCard>

      <WidgetCard
        title={`Timeline · ${total} eventos`}
        description={selectedEvent ? `Filtrado por ${selectedEvent}` : "Sin filtro"}
        citations={["automation_events collection"]}
        testId="widget-events-timeline"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar." />}
        {q.data && items.length === 0 && (
          <EmptyState message="Sin eventos que coincidan." testId="events-empty" />
        )}
        {items.length > 0 && (
          <ul className="space-y-0.5" data-testid="events-timeline-list">
            {items.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </ul>
        )}

        {total > PAGE_SIZE && (
          <div className="mt-3 flex items-center justify-between text-xs" data-testid="events-pagination">
            <span className="text-[var(--ag-text-dim)]">
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="events-page-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                <ChevronLeft className="h-3 w-3" /> Anterior
              </button>
              <span className="px-2 text-[10px] tabular-nums">
                pág {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                data-testid="events-page-next"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-0.5 text-[10px] disabled:opacity-30"
              >
                Siguiente <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["retention 30 días", "20 event_name canónicos"]} />
      </footer>
    </section>
  );
}
